import { getCoprincipalMailboxInfoForMailboxInfo } from "owa-account-source-list-store";
import { logCoreUsage } from "owa-analytics";
import { DrawerPane, isDrawerPaneVisible } from "owa-drawer";
import { isGlobalFeatureEnabled } from "owa-feature-flags";
import { getRewardsAccountKey, markRewardsPanelOpened } from "owa-header-rewards-medallion-store";
import type { OwaHeaderPaneProps } from "owa-header-types";
import loc from "owa-localize";
import { observer } from "owa-mobx-react";
import { getModuleContextMailboxInfo } from "owa-module-context-mailboxinfo";
import { useState } from "owa-react";
import { useCallback, useEffect, useRef, type FunctionComponent } from "react";
import RewardsCookieHost, { type RewardsHostStatus } from "./RewardsCookieHost";
import { microsoftRewardsTitle } from "./RewardsPane.locstring.json";
import { bodyContainer, header, headerTitle, logo, titleText } from "./RewardsPane.scss";

type RewardsPaneCloseMethod = "closeButton" | "headerButton" | "tabClose" | "other";

function logRewardsPaneClosed(openTimestamp: number, loadedTimestamp: number | undefined, closedWhileLoading: boolean, closeMethod: RewardsPaneCloseMethod) {
    const closeTimestamp = performance.now();
    const durationMs = Math.round(closeTimestamp - openTimestamp);
    const engagedMs = loadedTimestamp === undefined ? 0 : Math.round(closeTimestamp - loadedTimestamp);
    const moduleMailboxInfo = getModuleContextMailboxInfo();
    const telemetryMailboxInfo = getCoprincipalMailboxInfoForMailboxInfo(moduleMailboxInfo) ?? moduleMailboxInfo;
    logCoreUsage("RewardsPaneClosed", telemetryMailboxInfo, { durationMs, closedWhileLoading, CloseMethod: closeMethod, engagedMs }, {});
}

function useRewardsPaneDismissTelemetry(isVisible: boolean, status: RewardsHostStatus) {
    const statusRef = useRef(status);
    const openTimestampRef = useRef<number | undefined>(undefined);
    const loadedTimestampRef = useRef<number | undefined>(undefined);
    const closeMethodRef = useRef<RewardsPaneCloseMethod>("other");
    
    if (isVisible) {
        statusRef.current = status;
    }
    
    useEffect(() => {
        if (isVisible && openTimestampRef.current === undefined) {
            openTimestampRef.current = performance.now();
        }
        else if (!isVisible && openTimestampRef.current !== undefined) {
            logRewardsPaneClosed(openTimestampRef.current, loadedTimestampRef.current, statusRef.current === "loading", closeMethodRef.current === "other" ? "headerButton" : closeMethodRef.current);
            openTimestampRef.current = undefined;
            loadedTimestampRef.current = undefined;
            closeMethodRef.current = "other";
        }
    }, [isVisible]);
    
    useEffect(() => {
        if (isVisible &&
            status === "ready" &&
            loadedTimestampRef.current === undefined) {
            loadedTimestampRef.current = performance.now();
        }
    }, [isVisible, status]);
    
    useEffect(() => {
        const handleTabClose = () => {
            if (openTimestampRef.current === undefined) {
                return;
            }
            logRewardsPaneClosed(openTimestampRef.current, loadedTimestampRef.current, statusRef.current === "loading", "tabClose");
            openTimestampRef.current = undefined;
            loadedTimestampRef.current = undefined;
        };
        
        window.addEventListener("beforeunload", handleTabClose);
        return () => {
            window.removeEventListener("beforeunload", handleTabClose);
            if (openTimestampRef.current !== undefined) {
                logRewardsPaneClosed(openTimestampRef.current, loadedTimestampRef.current, statusRef.current === "loading", "other");
                openTimestampRef.current = undefined;
                loadedTimestampRef.current = undefined;
            }
        };
    }, []);
    
    return useCallback(() => {
        closeMethodRef.current = "closeButton";
    }, []);
}

const rewardsPaneTitle = () => {
    return (<span className={headerTitle}>
            <svg className={logo} width="16" height="16" viewBox="0 0 21 21" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
                <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
                <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
                <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
                <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
            </svg>
            <span className={titleText}>{loc(microsoftRewardsTitle)}</span>
        </span>);
};

const defaultExport: FunctionComponent<OwaHeaderPaneProps> = observer(function RewardsPane(props: OwaHeaderPaneProps) {
    const isVisible = isDrawerPaneVisible(props.paneId);
    const [status, setStatus] = useState("loading" as RewardsHostStatus);
    const onCloseButtonClick = useRewardsPaneDismissTelemetry(isVisible, status);
    const isRewardsCachingEnabled = isGlobalFeatureEnabled("growth-owaRewardsBadge");
    const onStatusChange = useCallback((nextStatus: RewardsHostStatus) => {
        setStatus(nextStatus);
    }, []);
    
    useEffect(() => {
        if (isVisible) {
            const accountKey = getRewardsAccountKey(getModuleContextMailboxInfo());
            markRewardsPanelOpened(accountKey);
        }
    }, [isVisible]);
    
    return (<DrawerPane paneId={props.paneId} bodyClassName={bodyContainer} headerText={rewardsPaneTitle()} headerClassName={header} noPadding={true} onCloseButtonClick={onCloseButtonClick} preserveContentOnDismiss={isRewardsCachingEnabled} role={"complementary"} width={380}>
            <RewardsCookieHost isVisible={isVisible} isCachingEnabled={isRewardsCachingEnabled} onStatusChange={onStatusChange}/>
        </DrawerPane>);
}, "RewardsPane");

export default defaultExport;
